import { hasConnectedMcpStatus } from '@littlebird/mcp';
import { useSDK } from '@littlebird/sdk';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { backendApis } from '@/backendApis';
import { ModalsManager } from '@/components/modals/modalsManager';
import { FEATURES, hasFeature } from '@/features/feature-flags';
import { integrationKeys } from '@/features/integrations/integrations.query';
import { connectGoogleSignupIntegrations } from '@/features/login/google-signup';
import {
  fetchGoogleSignupConsentConfig,
  trackGoogleConsentResult,
} from '@/features/login/google-signup-consent-config';
import { SocialLoginAbandonedError } from '@/features/login/social-login';
import { useMCPServersQuery } from '@/features/mcp/mcp.query';
import { MCPAddServerModal } from '@/features/mcp/MCPAddServerModal';
import { useViewportSize } from '@/hooks/useViewportSize';
import { trackToolkitConnected } from '@/integrations/integrations.utils';
import { useIntegrationState } from '@/integrations/useIntegrationState';
import { queryClient } from '@/query/client';
import { userStore } from '@/stores/user.store';
import { Routes } from '@/types/routes';
import { setUserProperties, trackEvent } from '@/utils/analytics';

import { useCompleteWebOnboarding } from '../web-onboarding.hooks';
import {
  openOAuthPopup,
  startIntegrationConnect,
} from '../web-onboarding.oauth';

import {
  CALENDAR_TOOLKIT,
  GMAIL_TOOLKIT,
  MORE_APPS,
  PREPARE_DURATION_MS,
  PREPARE_STEPS,
} from './feed-onboarding.config';

const TRACKING_VARIANT = 'feed';
const POPUP_CLOSED_POLL_MS = 500;
const SPLIT_LAYOUT_MIN_WIDTH = 1024;
const SPLIT_LAYOUT_LANDSCAPE_MIN_WIDTH = 768;

type FeedOnboardingStep = 'connect-google' | 'connect-more' | 'prepare';

const useConnectedToolkits = () => {
  const {
    integrationStates,
    availableIntegrations,
    connectedAccounts,
    loadError,
  } = useIntegrationState();
  const { data: mcpServers } = useMCPServersQuery();
  const [connectedThisSession, setConnectedThisSession] = useState<string[]>(
    []
  );

  const connectedToolkits = new Set([
    ...connectedThisSession,
    ...integrationStates
      .filter((integration) => integration.isConnected)
      .map((integration) => integration.info.toolkit_name),
    ...(connectedAccounts ?? [])
      .filter(
        (account) =>
          account.connection_status === 'connected' ||
          account.connection_status === 'probing'
      )
      .map((account) => account.toolkit_name),
    ...(mcpServers ?? [])
      .filter(hasConnectedMcpStatus)
      .map((server) => server.slug),
  ]);

  const markConnected = useCallback(
    (toolkitName: string) =>
      setConnectedThisSession((previous) => [...previous, toolkitName]),
    []
  );

  return {
    hasLoadedConnections:
      availableIntegrations !== undefined &&
      connectedAccounts !== undefined &&
      mcpServers !== undefined,
    isLoadingIntegrations: !availableIntegrations && !loadError,
    connectedToolkits,
    markConnected,
  };
};

const useToolkitConnect = (
  onConnected: (toolkitName: string) => void,
  source: string
) => {
  const [connectingToolkit, setConnectingToolkit] = useState<string | null>(
    null
  );
  // A second tap can land before the re-render, so the guard is a ref
  const connectingRef = useRef<string | null>(null);

  const connect = useCallback(
    (toolkitName: string) => {
      if (connectingRef.current) return;
      if (source === 'web_onboarding_feed') {
        trackEvent('WEB_ONBOARDING_CHOICE_CLICKED', {
          choice: toolkitName,
          variant: TRACKING_VARIANT,
        });
      }

      const popup = openOAuthPopup('about:blank');
      connectingRef.current = toolkitName;
      setConnectingToolkit(toolkitName);
      const stopConnecting = () => {
        if (connectingRef.current === toolkitName) connectingRef.current = null;
        setConnectingToolkit((current) =>
          current === toolkitName ? null : current
        );
      };
      const closedPoll = setInterval(() => {
        if (popup && !popup.closed) return;
        clearInterval(closedPoll);
        stopConnecting();
      }, POPUP_CLOSED_POLL_MS);

      void startIntegrationConnect(toolkitName, popup, source, {
        quickGenerateInitialSuggestions: true,
      }).then((success) => {
        clearInterval(closedPoll);
        stopConnecting();
        if (success) onConnected(toolkitName);
      });
    },
    [onConnected, source]
  );

  const connectMcp = useCallback(
    ({
      toolkitName,
      name,
      url,
    }: {
      toolkitName: string;
      name: string;
      url: string;
    }) => {
      if (connectingToolkit) return;
      if (source === 'web_onboarding_feed') {
        trackEvent('WEB_ONBOARDING_CHOICE_CLICKED', {
          choice: toolkitName,
          variant: TRACKING_VARIANT,
        });
      }
      setConnectingToolkit(toolkitName);
      ModalsManager.show(MCPAddServerModal, {
        initialUrl: url,
        initialName: name,
        onOAuthSettled: (success: boolean) => {
          setConnectingToolkit(null);
          if (success) onConnected(toolkitName);
        },
      });
    },
    [connectingToolkit, onConnected, source]
  );

  return { connectingToolkit, connect, connectMcp };
};

const useSeedFeed = (connectedAppCount: number) => {
  useEffect(() => {
    if (connectedAppCount === 0) return;
    void backendApis.integrations.startInitialSuggestions().catch((error) => {
      console.error('Starting initial feed suggestions failed:', error);
    });
  }, [connectedAppCount]);
};

export const useFeedAppConnections = (source = 'web_onboarding_feed') => {
  const {
    isLoadingIntegrations,
    hasLoadedConnections,
    connectedToolkits,
    markConnected,
  } = useConnectedToolkits();
  const { connectingToolkit, connect, connectMcp } = useToolkitConnect(
    markConnected,
    source
  );

  const toApp = (toolkitName: string, name: string) => ({
    toolkitName,
    name,
    isConnected: connectedToolkits.has(toolkitName),
    isConnecting: connectingToolkit === toolkitName,
    connect: () => connect(toolkitName),
  });

  const googleApps = [
    toApp(GMAIL_TOOLKIT, 'Gmail'),
    toApp(CALENDAR_TOOLKIT, 'Google Calendar'),
  ];

  const { data: googleConsentConfig, isPending: isConsentConfigPending } =
    useQuery({
      queryKey: ['google-signup', 'consent-config', userStore.user?.id],
      queryFn: fetchGoogleSignupConsentConfig,
      enabled: source === 'web_onboarding_feed',
      staleTime: Infinity,
    });
  const consentFlow = googleConsentConfig?.flow;
  useEffect(() => {
    // Funnels split the consent outcomes by this property once the user is known
    if (consentFlow) setUserProperties({ google_consent_flow: consentFlow });
  }, [consentFlow]);
  const [isConnectingGoogle, setIsConnectingGoogle] = useState(false);
  const connectingGoogle = useRef(false);
  const connectGoogle = async () => {
    if (!googleConsentConfig || connectingGoogle.current) return;
    connectingGoogle.current = true;
    setIsConnectingGoogle(true);
    trackEvent('GOOGLE_CONSENT_STARTED', {
      flow: 'consent-after-sign-in',
      surface: 'onboarding',
    });
    const consentResult = {
      flow: 'consent-after-sign-in',
      surface: 'onboarding',
      requestedToolkits: googleConsentConfig.toolkits,
    } as const;
    try {
      const connected = await connectGoogleSignupIntegrations(
        googleConsentConfig,
        userStore.user?.email ?? ''
      );
      trackGoogleConsentResult({
        ...consentResult,
        outcome: connected.length > 0 ? 'connected' : 'nothing_connected',
        connectedToolkits: connected,
      });
      for (const toolkit of connected) {
        markConnected(toolkit);
        trackToolkitConnected(toolkit, source);
      }
      await queryClient.invalidateQueries({ queryKey: integrationKeys.all });
      if (connected.length === 0)
        toast.info(
          'No new Google integrations were connected. Please try again.'
        );
    } catch (error) {
      const isAbandoned = error instanceof SocialLoginAbandonedError;
      trackGoogleConsentResult({
        ...consentResult,
        outcome: isAbandoned ? 'abandoned' : 'failed',
        connectedToolkits: [],
      });
      if (!isAbandoned)
        toast.error(
          error instanceof Error
            ? error.message
            : 'Could not connect Google integrations.'
        );
    } finally {
      connectingGoogle.current = false;
      setIsConnectingGoogle(false);
    }
  };

  const moreApps = MORE_APPS.map((app) => ({
    ...toApp(app.toolkitName, app.name),
    connect: () => (app.url ? connectMcp(app) : connect(app.toolkitName)),
  }));

  return {
    googleApps,
    googleConsent: {
      isLoading: isConsentConfigPending,
      canConnect:
        googleConsentConfig?.flow === 'consent-after-sign-in' &&
        googleConsentConfig.toolkits.some(
          (toolkit) => !connectedToolkits.has(toolkit)
        ),
      isConnecting: isConnectingGoogle,
      connect: connectGoogle,
    },
    moreApps,
    connectedToolkits,
    hasLoadedConnections,
    isLoadingMoreApps: isLoadingIntegrations,
  };
};

export const useFeedOnboardingFlow = () => {
  const sdk = useSDK();
  const completeOnboarding = useCompleteWebOnboarding();
  const {
    googleApps,
    googleConsent,
    moreApps,
    connectedToolkits,
    isLoadingMoreApps,
  } = useFeedAppConnections();
  useSeedFeed(connectedToolkits.size);
  const [step, setStep] = useState<FeedOnboardingStep>('connect-google');
  const [isFinishing, setIsFinishing] = useState(false);
  const [hasFinishFailed, setHasFinishFailed] = useState(false);

  useEffect(() => {
    trackEvent('WEB_ONBOARDING_STARTED', { variant: TRACKING_VARIANT });
    // PostHog flags target the feed cohort by this property, which stays set after the desktop app is installed
    setUserProperties({ web_onboarding_variant: TRACKING_VARIANT });
    void sdk.initialize().catch(() => {});
  }, [sdk]);

  // Keeps the step while the user is on it, even if the list turns out empty.
  const showsMoreStep =
    step === 'connect-more' || isLoadingMoreApps || moreApps.length > 0;
  const steps: FeedOnboardingStep[] = showsMoreStep
    ? ['connect-google', 'connect-more', 'prepare']
    : ['connect-google', 'prepare'];
  const stepIndex = steps.indexOf(step);

  const finish = async () => {
    if (isFinishing) return;
    setIsFinishing(true);
    setHasFinishFailed(false);
    try {
      trackEvent('WEB_ONBOARDING_COMPLETED', {
        integrations: [...connectedToolkits],
        variant: TRACKING_VARIANT,
      });
      await completeOnboarding(
        [],
        hasFeature(FEATURES.ACTION_CARDS_FEED) ? Routes.Chats : undefined
      );
    } catch (error) {
      console.error('Feed onboarding completion failed:', error);
      setHasFinishFailed(true);
      setIsFinishing(false);
    }
  };

  return {
    step,
    stepIndex,
    stepCount: steps.length,
    goBack: () => setStep(steps[stepIndex - 1]),
    goNext: () => setStep(steps[stepIndex + 1]),
    googleApps,
    googleConsent,
    hasConnectedGoogleApps: googleApps.some((app) => app.isConnected),
    moreApps,
    isLoadingMoreApps,
    hasConnectedMoreApps: moreApps.some((app) => app.isConnected),
    hasConnectedApps: connectedToolkits.size > 0,
    isFinishing,
    hasFinishFailed,
    finish,
  };
};

/**
 * Two columns on wide or landscape screens; a single column on phones and
 * portrait tablets, where two columns squeeze the demo and the copy.
 */
export const useFeedOnboardingLayout = () => {
  const { width, height } = useViewportSize();
  const isSplit =
    width >= SPLIT_LAYOUT_MIN_WIDTH ||
    (width >= SPLIT_LAYOUT_LANDSCAPE_MIN_WIDTH && width > height);
  const hasRoomForRegularDemo = isSplit
    ? width >= SPLIT_LAYOUT_MIN_WIDTH
    : width >= SPLIT_LAYOUT_LANDSCAPE_MIN_WIDTH;
  return {
    isSplit,
    demoSize: hasRoomForRegularDemo
      ? ('regular' as const)
      : ('compact' as const),
  };
};

/** Records each showing of the combined Gmail and Calendar button */
export const useGoogleConsentOfferTracking = () => {
  useEffect(() => {
    trackEvent('GOOGLE_CONSENT_OFFERED', {
      flow: 'consent-after-sign-in',
      surface: 'onboarding',
    });
  }, []);
};

/** Moves focus to a step's heading so screen readers announce the new step. */
export const useFocusOnMount = <T extends HTMLElement>() => {
  const ref = useRef<T>(null);
  useEffect(() => ref.current?.focus({ preventScroll: true }), []);
  return ref;
};

export const usePrepareProgress = () => {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const stepMs = PREPARE_DURATION_MS / PREPARE_STEPS.length;
    const timers = PREPARE_STEPS.map((_, index) =>
      setTimeout(() => setActiveIndex(index + 1), stepMs * (index + 1))
    );
    return () => timers.forEach(clearTimeout);
  }, []);

  return activeIndex;
};
